import { Flex, Icon, Spinner } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { LabelV2Version } from 'types/releases';
import { ReactComponent as Checkmark } from 'resources/images/checkmark.svg';
import { ReactComponent as InfoSvg } from 'resources/images/info.svg';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getReleaseGuid,
  getReleaseVersionLink,
  getVersionSyncStatus,
} from 'app/store/Releases/selectors';
import { fetchReleaseVersionEffect } from 'app/store/Releases/effects';
import { ReleaseIssuesDialog } from '../ReleaseIssuesDialog';

export interface DataCompProps {
  version: LabelV2Version;
}

export const DataComp = ({ version }: DataCompProps) => {
  const dispatch = useAppDispatch();
  const releaseGuid = useAppSelector(getReleaseGuid);
  const releaseVersionLink = useAppSelector(getReleaseVersionLink);
  const syncStatus = useAppSelector((state) =>
    getVersionSyncStatus(state, version.guid)
  );

  const [isLoading, setIsLoading] = useState(syncStatus);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!syncStatus && isLoading) {
      const refreshVersion = async () => {
        await dispatch(
          fetchReleaseVersionEffect({
            versionGuid: version.guid,
            songGuid: version.song.guid,
            releaseGuid,
            href: releaseVersionLink,
          })
        );
        setIsLoading(false);
      };
      refreshVersion();
    }
  }, [
    dispatch,
    isLoading,
    releaseGuid,
    releaseVersionLink,
    syncStatus,
    version.guid,
    version.song,
  ]);

  if (!version.validationErrors || syncStatus) {
    return <Spinner minW={5} minH={5} />;
  }

  const errors = version.validationErrors ?? [];
  if (errors.length === 0) {
    return <Icon as={Checkmark} width={5} height={5} color="G.80" />;
  }

  const label = errors?.length > 1 ? `${errors.length} Issues` : '1 Issue';

  return (
    <Flex
      gap={2}
      alignItems="center"
      color="R.80"
      role="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (errors.length > 0) {
          setOpen(true);
        }
      }}
    >
      <Icon as={InfoSvg} width={5} height={5} color="R.80" />
      {label}
      <ReleaseIssuesDialog
        isOpen={open}
        version={version}
        onClose={() => {
          setOpen(false);
        }}
      />
    </Flex>
  );
};
